// Iter AY — admin feedback triage dashboard.
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Bug, HelpCircle, Lightbulb, MessageCircle, Image as ImageIcon, Save, Search } from "lucide-react";

const TYPES = [
  { key: "bug", label: "Bug", icon: Bug, cls: "bg-terracotta-100 text-terracotta-700" },
  { key: "confusing", label: "Confusing", icon: HelpCircle, cls: "bg-amber-100 text-amber-700" },
  { key: "suggestion", label: "Suggestion", icon: Lightbulb, cls: "bg-sage-100 text-sage-700" },
  { key: "other", label: "Other", icon: MessageCircle, cls: "bg-paper-100 text-ink-700" },
];
const STATUSES = ["new", "reviewing", "planned", "fixed", "closed"];
const STATUS_CLS = {
  new: "bg-sky-100 text-sky-700",
  reviewing: "bg-amber-100 text-amber-700",
  planned: "bg-violet-100 text-violet-700",
  fixed: "bg-sage-100 text-sage-700",
  closed: "bg-paper-100 text-ink-500",
};

function FeedbackRow({ item, onUpdated }) {
  const [notes, setNotes] = useState(item.admin_notes || "");
  const [shot, setShot] = useState(null);
  const [loadingShot, setLoadingShot] = useState(false);
  const t = TYPES.find((x) => x.key === item.type) || TYPES[3];
  const Icon = t.icon;

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/feedback/${item.id}`, { status });
      onUpdated(r.data);
      window.dispatchEvent(new Event("pstr-feedback-badge"));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const saveNotes = async () => {
    try {
      const r = await api.patch(`/feedback/${item.id}`, { admin_notes: notes });
      onUpdated(r.data);
      toast.success("Notes saved");
    } catch { toast.error("Could not save notes"); }
  };

  const loadShot = async () => {
    setLoadingShot(true);
    try {
      const r = await api.get(`/feedback/${item.id}`);
      setShot(r.data.screenshot);
    } catch { toast.error("Could not load screenshot"); }
    finally { setLoadingShot(false); }
  };

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid={`feedback-item-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`min-h-[26px] px-2.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${t.cls}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
        </span>
        <select
          data-testid={`feedback-status-select-${item.id}`}
          value={item.status}
          onChange={(e) => setStatus(e.target.value)}
          className={`min-h-[26px] px-2 rounded-full text-xs font-medium border-0 outline-none cursor-pointer ${STATUS_CLS[item.status]}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <span className="text-xs text-ink-500 ml-auto">{new Date(item.created_at).toLocaleString()}</span>
      </div>
      <p className="text-sm text-ink-900 whitespace-pre-wrap mb-3" data-testid="feedback-item-comment">{item.comment}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500 mb-3">
        <span>{item.user_name} · {item.user_email}</span>
        <span className="font-mono">{item.page_path || "—"}</span>
        <span>{item.screen}</span>
        <span className="font-mono">{item.app_version}</span>
        <span className="truncate max-w-[280px]" title={item.user_agent}>{item.user_agent}</span>
      </div>
      {item.has_screenshot && !shot && (
        <button data-testid={`feedback-view-screenshot-${item.id}`} onClick={loadShot} disabled={loadingShot} className="mb-3 min-h-[34px] px-3 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 text-xs inline-flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> {loadingShot ? "Loading…" : "View screenshot"}
        </button>
      )}
      {shot && <img src={shot} alt="Feedback screenshot" className="rounded-2xl border border-paper-200 max-h-72 mb-3" data-testid="feedback-screenshot-full" />}
      <div className="flex gap-2 items-start">
        <textarea
          data-testid={`feedback-notes-${item.id}`}
          rows={1}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Private admin notes…"
          className="flex-1 px-3 py-2 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 outline-none text-xs text-ink-900 resize-y"
        />
        <button data-testid={`feedback-save-notes-${item.id}`} onClick={saveNotes} className="min-h-[34px] px-3 rounded-full bg-ink-900 text-white text-xs inline-flex items-center gap-1.5 shrink-0">
          <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Save
        </button>
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/feedback", { params })
      .then((r) => { setItems(r.data.items); setCounts(r.data.counts); })
      .catch(() => toast.error("Could not load feedback"))
      .finally(() => setLoading(false));
  }, [typeFilter, statusFilter, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);
  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const onUpdated = (updated) => setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated, has_screenshot: i.has_screenshot } : i)));

  const chip = (active) => `min-h-[34px] px-3 rounded-full text-xs font-medium border transition-colors ${active ? "bg-ink-900 text-white border-ink-900" : "bg-white border-paper-200 text-ink-700 hover:bg-paper-100"}`;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-feedback-page">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-subnav">
          <Link to="/admin/usage" data-testid="admin-subnav-usage" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">AI Usage</Link>
          <Link to="/admin/production" data-testid="admin-subnav-production" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Production</Link>
          <Link to="/admin/billing" data-testid="admin-subnav-billing" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Billing & Verification</Link>
          <Link to="/admin/feedback" data-testid="admin-subnav-feedback" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-ink-900 text-white inline-flex items-center gap-2">Feedback</Link>
        </div>

        <h1 className="font-heading text-3xl text-ink-900 mb-1">Beta feedback</h1>
        <p className="text-sm text-ink-500 mb-6">{counts.new || 0} new · {items.length} shown</p>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6" data-testid="feedback-stats">
          {[["Total", counts.total, "total"], ["New", counts.new, "new"], ["Reviewing", counts.reviewing, "reviewing"], ["Planned", counts.planned, "planned"], ["Fixed", counts.fixed, "fixed"], ["Closed", counts.closed, "closed"]].map(([label, val, key]) => (
            <div key={key} className="bg-white border border-paper-200 rounded-2xl px-3 py-3 text-center" data-testid={`stat-${key}`}>
              <div className="font-heading text-xl text-ink-900" data-testid={`stat-${key}-value`}>{val || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-ink-500 absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              data-testid="feedback-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, or keyword…"
              className="w-full min-h-[40px] pl-10 pr-4 rounded-full bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm text-ink-900"
            />
          </div>
          <input data-testid="feedback-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="min-h-[40px] px-3 rounded-full bg-white border border-paper-200 text-xs text-ink-700 outline-none focus:ring-2 focus:ring-sage-500/30" />
          <span className="text-xs text-ink-500">to</span>
          <input data-testid="feedback-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="min-h-[40px] px-3 rounded-full bg-white border border-paper-200 text-xs text-ink-700 outline-none focus:ring-2 focus:ring-sage-500/30" />
          {(dateFrom || dateTo || search) && (
            <button data-testid="feedback-clear-filters" onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); }} className="min-h-[40px] px-3 rounded-full text-xs text-ink-500 hover:text-ink-900 underline underline-offset-4">
              Clear
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="feedback-type-filters">
          <button className={chip(!typeFilter)} onClick={() => setTypeFilter("")} data-testid="filter-type-all">All types</button>
          {TYPES.map((t) => (
            <button key={t.key} className={chip(typeFilter === t.key)} onClick={() => setTypeFilter(t.key)} data-testid={`filter-type-${t.key}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="feedback-status-filters">
          <button className={chip(!statusFilter)} onClick={() => setStatusFilter("")} data-testid="filter-status-all">All statuses</button>
          {STATUSES.map((s) => (
            <button key={s} className={chip(statusFilter === s)} onClick={() => setStatusFilter(s)} data-testid={`filter-status-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}{counts[s] ? ` · ${counts[s]}` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-ink-500 text-sm animate-pulse py-10 text-center">Loading feedback…</div>
        ) : items.length === 0 ? (
          <div className="text-ink-500 text-sm py-10 text-center bg-white border border-paper-200 rounded-3xl" data-testid="feedback-empty">No feedback matches these filters yet.</div>
        ) : (
          <div className="space-y-4">
            {items.map((i) => <FeedbackRow key={i.id} item={i} onUpdated={onUpdated} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
